import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import { Button, Disclosure, Icon, SectionLabel } from "../kit";
import { useRing } from "./ringContext";

/**
 * ISO/IEC 7810 ID-1 — every bank, credit and ID card on earth is
 * 85.60 × 53.98 mm. That is the whole trick behind "hold any card against the
 * screen": the card is a ruler of known length, so matching two on-screen rules
 * to it yields this display's px-per-mm without asking anyone for their DPI.
 */
const CARD_LONG_MM = 85.6;
const CARD_SHORT_MM = 53.98;

/** CSS's `mm` is a fixed 96-dpi fiction, but it lands within a third of any real
 * display — a far better place to start dragging than a bare pixel count. */
const CSS_PX_PER_MM = 96 / 25.4;

/** Drag limits. The card box spans phone (~5 px/mm) to a scaled 4K panel; the
 * circle spans every finger a ring is made for. */
const CARD_PX_RANGE = [110, 460] as const;
const RING_MM_RANGE = [11, 26] as const;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * Find Your Size — the two-step measuring wizard.
 *
 * Step 1 sends the shopper for a ring they own; step 2 calibrates the screen
 * against a bank card; step 3 has them lay that ring over a circle sized in the
 * now-known millimetres and writes the matching size through `setRingSize`.
 * The three prose methods (paper strip, caliper, jeweller) stay as a secondary
 * block for anyone who would rather not measure on glass.
 *
 * Same modal shell as `SpecSheet` (portal + backdrop + Esc), so the two read as
 * one product.
 */
export function SizeHelpModal({ onClose }: { onClose: () => void }) {
  const { state, catalogs, r, apply } = useRing();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [cardPx, setCardPx] = useState(CARD_SHORT_MM * CSS_PX_PER_MM);
  const pxPerMM = cardPx / CARD_SHORT_MM;
  // Start the circle on the ring currently being built, so the shopper adjusts
  // from something real rather than from an arbitrary blob.
  const [ringMM, setRingMM] = useState(state.spec?.innerDiameterMM ?? 17.3);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => { if (ev.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const cardDrag = useHandleDrag("y", (px) => setCardPx((c) => clamp(c + px, CARD_PX_RANGE[0], CARD_PX_RANGE[1])));
  const ringDrag = useHandleDrag("x", (px) => setRingMM((mm) => clamp(mm + px / pxPerMM, RING_MM_RANGE[0], RING_MM_RANGE[1])));

  const match = catalogs.ringSizes.length
    ? catalogs.ringSizes.reduce((a, b) => (Math.abs(b.diameterMM - ringMM) < Math.abs(a.diameterMM - ringMM) ? b : a))
    : null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Find your ring size"
      onClick={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-on-surface/40 p-6 backdrop-blur-sm"
    >
      <div className="my-auto w-full max-w-[560px] rounded-2xl border border-outline-variant/40 bg-surface-container-lowest p-7 shadow-[0_24px_80px_rgba(0,0,0,0.18)]">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-headline-md text-primary">Find your size</h2>
            <p className="font-body text-[12px] text-on-surface-variant">Step {step} of 3</p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-variant/50"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {step === 1 ? (
          <div className="mt-5 flex flex-col gap-5">
            <div className="rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-4">
              <SectionLabel>You will need</SectionLabel>
              <ul className="mt-2 flex list-disc flex-col gap-1 pl-4 font-body text-[11.5px] leading-relaxed text-on-surface-variant">
                <li>A ring you already own — a slim, flat band that sits comfortably on the right finger. Keep it within reach.</li>
                <li>Any bank, credit or store card. They are all the same physical size, which is what makes one a ruler.</li>
              </ul>
            </div>
            <Disclosure label="Other ways to measure" value="3 methods">
              <div className="flex flex-col gap-5 pt-1">
                <Method
                  icon="straighten"
                  title="Printable paper strip"
                  steps={[
                    "Cut a strip of paper about 6 mm wide and 100 mm long.",
                    "Wrap it around the base of the finger — snug, but it must slide over the knuckle.",
                    "Mark where the strip overlaps, lay it flat and measure to the mark in millimetres.",
                    "That length is the circumference: divide by π (3.14) for the inner diameter, then pick the nearest size above.",
                  ]}
                />
                <Method
                  icon="donut_large"
                  title="Measure a ring that already fits"
                  steps={[
                    "Choose a ring worn on the same finger.",
                    "Lay it flat and measure straight across the INSIDE, edge to edge, in millimetres.",
                    "Match that inner diameter to the sizes in the dropdown — each one prints its Ø.",
                    "A wide band sits tighter than a narrow one: at 6 mm and up, go a quarter to a half size larger.",
                  ]}
                />
                <Method
                  icon="storefront"
                  title="Professional sizing"
                  steps={[
                    "Any jeweller will size a finger free of charge with a steel mandrel and ring gauges.",
                    "This is the only method good to a quarter size, and the one to use for a ring that will not be resized.",
                  ]}
                />
                <div className="rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-3">
                  <SectionLabel>Before you measure</SectionLabel>
                  <ul className="mt-2 flex list-disc flex-col gap-1 pl-4 font-body text-[11.5px] leading-relaxed text-on-surface-variant">
                    <li>Fingers are smallest in the cold morning and largest in the evening heat — measure warm, late in the day.</li>
                    <li>Measure the finger the ring will actually be worn on; hands differ by up to half a size.</li>
                    <li>Check the knuckle: the ring has to pass it, so size to the knuckle if it is the wider of the two.</li>
                    <li>Take the measurement three times and use the largest reading.</li>
                  </ul>
                </div>
              </div>
            </Disclosure>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="mt-5 flex flex-col gap-4">
            <p className="font-body text-[12px] leading-relaxed text-on-surface-variant">
              Any bank card can act as a ruler, since all cards share one physical size. Hold a card
              against the screen with its top edge on the upper line, then drag the arrow until the
              lower line sits flush with the card.
            </p>
            <div className="flex justify-center py-2">
              <div
                className="relative"
                style={{ width: cardPx * (CARD_LONG_MM / CARD_SHORT_MM), height: cardPx }}
              >
                <span aria-hidden="true" className="absolute -left-5 -right-5 top-0 h-px bg-primary" />
                <span aria-hidden="true" className="absolute -left-5 -right-5 bottom-0 h-px bg-primary" />
                <div className="absolute inset-0 flex flex-col justify-end gap-1 rounded-xl border border-dashed border-outline-variant bg-surface-container-low p-3">
                  <span className="font-body text-[13px] tracking-[0.22em] text-outline">•••• •••• •••• ••••</span>
                  <span className="font-display text-[8px] font-semibold uppercase tracking-[0.18em] text-outline">Credit card</span>
                </div>
                <button
                  type="button"
                  aria-label="Card height — drag or use the arrow keys"
                  className="absolute -bottom-4 left-1/2 flex h-8 w-8 -translate-x-1/2 cursor-ns-resize touch-none items-center justify-center rounded-full border border-outline-variant bg-surface-container-lowest text-primary shadow-sm"
                  {...cardDrag}
                >
                  <Icon name="height" size={16} />
                </button>
              </div>
            </div>
            <span className="text-center font-body text-[11px] tabular-nums text-on-surface-variant">
              1 mm = {pxPerMM.toFixed(1)} px on this screen
            </span>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="mt-5 flex flex-col gap-4">
            <p className="font-body text-[12px] leading-relaxed text-on-surface-variant">
              Lay your ring flat on the screen and drag the arrow until the circle sits inside it,
              touching the inner edge all the way round.
            </p>
            <div className="flex justify-center py-2">
              <div className="relative" style={{ width: ringMM * pxPerMM + 40, height: ringMM * pxPerMM + 8 }}>
                <div
                  aria-hidden="true"
                  className="absolute left-0 top-1 rounded-full border-2 border-dashed border-secondary"
                  style={{ width: ringMM * pxPerMM, height: ringMM * pxPerMM }}
                />
                <button
                  type="button"
                  aria-label="Ring diameter — drag or use the arrow keys"
                  className="absolute flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize touch-none items-center justify-center rounded-full border border-outline-variant bg-surface-container-lowest text-primary shadow-sm"
                  style={{ left: ringMM * pxPerMM, top: ringMM * pxPerMM / 2 + 4 }}
                  {...ringDrag}
                >
                  <Icon name="width" size={16} />
                </button>
              </div>
            </div>
            <div className="rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-3 text-center">
              <span className="font-body text-[12px] tabular-nums text-on-surface-variant">
                Ø {ringMM.toFixed(1)} mm inner
                {match ? ` · closest size US ${match.size} · EU ${match.eu}` : ""}
              </span>
            </div>
            <span className="font-body text-[11px] leading-relaxed text-outline">
              A wide band sits tighter than a narrow one: at 6 mm and up, take the next size larger.
            </span>
          </div>
        ) : null}

        <div className="mt-6 flex items-center justify-between gap-3">
          {step > 1 ? (
            <Button variant="ghost" onClick={() => setStep((s) => (s === 3 ? 2 : 1))}>
              <Icon name="arrow_back" size={16} /> Back
            </Button>
          ) : <span />}
          {step === 1 ? <Button variant="primary" onClick={() => setStep(2)}>Start</Button> : null}
          {step === 2 ? <Button variant="primary" onClick={() => setStep(3)}>Continue</Button> : null}
          {step === 3 ? (
            <Button
              variant="primary"
              disabled={!match}
              onClick={async () => {
                if (match) await apply(r.setRingSize(match.size));
                onClose();
              }}
            >
              Use US {match?.size ?? "—"}
            </Button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * The measuring handles: pointer-capture drag reporting incremental px, plus
 * arrow-key nudges (Shift ×10). They are the only real inputs in this modal, so
 * they have to work without a mouse — a drag-only ruler is unusable on a
 * keyboard and unreachable for a screen reader.
 */
function useHandleDrag(axis: "x" | "y", onDelta: (px: number) => void) {
  const last = useRef(0);
  const read = (ev: ReactPointerEvent<HTMLElement>) => (axis === "y" ? ev.clientY : ev.clientX);
  return {
    onPointerDown: (ev: ReactPointerEvent<HTMLElement>) => {
      ev.preventDefault(); // no text selection mid-drag
      ev.currentTarget.focus(); // preventDefault would otherwise skip it, and the arrow keys need it
      ev.currentTarget.setPointerCapture(ev.pointerId);
      last.current = read(ev);
    },
    onPointerMove: (ev: ReactPointerEvent<HTMLElement>) => {
      if (!ev.currentTarget.hasPointerCapture(ev.pointerId)) return;
      // One axis only: a diagonal drag must not feed the other one's wobble into
      // a measurement someone's ring size comes out of.
      const now = read(ev);
      onDelta(now - last.current);
      last.current = now;
    },
    onKeyDown: (ev: ReactKeyboardEvent<HTMLElement>) => {
      const dir = ev.key === "ArrowDown" || ev.key === "ArrowRight" ? 1
        : ev.key === "ArrowUp" || ev.key === "ArrowLeft" ? -1 : 0;
      if (!dir) return;
      ev.preventDefault();
      onDelta(dir * (ev.shiftKey ? 10 : 1));
    },
  };
}

function Method({ icon, title, steps }: { icon: string; title: string; steps: string[] }) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-outline-variant/50 text-on-surface-variant">
        <Icon name={icon} size={17} />
      </span>
      <div className="flex min-w-0 flex-col gap-1.5">
        <span className="font-display text-[11px] font-semibold uppercase tracking-[0.12em] text-primary">
          {title}
        </span>
        <ol className="flex list-decimal flex-col gap-1 pl-4 font-body text-[11.5px] leading-relaxed text-on-surface-variant">
          {steps.map((s) => <li key={s}>{s}</li>)}
        </ol>
      </div>
    </div>
  );
}
